import { ChipProps, extendVariants, Chip as HeroChip } from '@heroui/react';
import { X } from 'lucide-react';
import type { AnchorHTMLAttributes, ElementType } from 'react';
import { twMerge } from 'tailwind-merge';

import { HeroBaseProps } from '../utils';

// For all Chip props see https://v2.heroui.com/docs/components/chip
export const Chip = extendVariants(
  (
    props: HeroBaseProps<
      ChipProps,
      'size' | 'radius' | 'color' | 'variant' | 'startContent' | 'endContent' | 'isDisabled'
    > & {
      size?: Extract<ChipProps['size'], '2xs' | 'xs' | 'sm' | 'md'>;
      radius?: Extract<ChipProps['radius'], 'sm' | 'full'>;
      color?: Exclude<ChipProps['color'], 'default'>;
      variant?: Extract<ChipProps['variant'], 'bordered' | 'flat' | 'solid' | 'faded'>;
      /**
       * Custom end content. When set, it takes precedence over the default close
       * icon — the `X` is not rendered even if `onClose` is provided.
       */
      endContent?: ChipProps['endContent'];
      as?: ElementType;
      href?: string;
    } & Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'>,
  ) => (
    <HeroChip
      {...props}
      endContent={props.endContent ?? (props.onClose != null ? <X className="shrink-0" /> : null)}
      classNames={{
        ...props.classNames,
        // A chip never outgrows its container: it stops at the available width and its label
        // ellipsises, so the pill and the close affordance stay whole. The label needs its own
        // element because `content` is a flex container, and `text-overflow` does not apply to
        // an anonymous flex item. `min-w-0` lets both shrink past their content width.
        base: twMerge('gap-1 max-w-full min-w-0', props.classNames?.base),
        content: twMerge('!px-0 flex items-center font-medium min-w-0', props.classNames?.content),
        closeButton: twMerge('flex shrink-0 text-inherit', props.classNames?.closeButton),
      }}>
      <span className="truncate">{props.children}</span>
    </HeroChip>
  ),
  {
    variants: {
      size: {
        // `[&_svg]:size-*` pins start/end (and close X) icons to a fixed size per chip size,
        // overriding whatever size the icon component renders at by default.
        '2xs': {
          base: 'px-1.5 h-5 text-xs [&_svg]:size-3',
          closeButton: 'pl-px',
        },
        xs: {
          base: 'px-2 h-6 text-sm [&_svg]:size-3',
          closeButton: 'pl-px',
        },
        sm: {
          base: 'px-2.5 h-7 text-sm [&_svg]:size-3',
          closeButton: 'pl-px',
        },
        md: {
          base: 'px-2.5 h-8 text-sm [&_svg]:size-4',
          closeButton: 'pl-px',
        },
      },
      variant: {
        bordered: {
          base: 'border',
        },
        faded: {
          base: 'border !border-dashed !bg-transparent',
        },
      },
      radius: {
        // Override the default `sm` radius (HeroUI resolves this to 8px) to 6px.
        // Other radius values (e.g. `full`) fall through to HeroUI so pill chips stay pills.
        sm: {
          base: '!rounded-md',
        },
      },
      color: {
        secondary: {
          base: 'text-secondary-700 bg-secondary-300',
        },
        primary: {
          base: 'text-primary-700 bg-primary-300',
        },
        warning: {
          base: 'text-warning-700 bg-warning-300',
        },
        success: {
          base: 'text-success-700 bg-success-300',
        },
        danger: {
          base: 'text-danger-700 bg-danger-300',
        },
      },
    },
    defaultVariants: {
      radius: 'sm',
      size: 'sm',
      color: 'secondary',
    },
    compoundVariants: [
      {
        variant: 'bordered',
        color: 'primary',
        class: '!bg-transparent border-primary-300',
      },
      {
        variant: 'bordered',
        color: 'secondary',
        class: '!bg-transparent border-secondary-300',
      },
      {
        variant: 'bordered',
        color: 'warning',
        class: '!bg-transparent border-warning-300',
      },
      {
        variant: 'bordered',
        color: 'success',
        class: '!bg-transparent border-success-300',
      },
      {
        variant: 'bordered',
        color: 'danger',
        class: '!bg-transparent border-danger-300',
      },
      // flat combos
      {
        variant: 'flat',
        color: 'primary',
        class: 'bg-primary-100',
      },
      {
        variant: 'flat',
        color: 'secondary',
        class: 'bg-secondary-200',
      },
      {
        variant: 'flat',
        color: 'warning',
        class: 'bg-warning-200',
      },
      {
        variant: 'flat',
        color: 'success',
        class: 'bg-success-200',
      },
      {
        variant: 'flat',
        color: 'danger',
        class: 'bg-danger-200',
      },
      // Faded combos
      {
        variant: 'faded',
        color: 'primary',
        class: 'border-primary-300',
      },
      {
        variant: 'faded',
        color: 'secondary',
        class: 'border-secondary-300',
      },
      {
        variant: 'faded',
        color: 'warning',
        class: 'border-warning-300',
      },
      {
        variant: 'faded',
        color: 'success',
        class: 'border-success-300',
      },
      {
        variant: 'faded',
        color: 'danger',
        class: 'border-danger-300',
      },
    ],
  },
);
